import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate, useParams, Link, useLocation } from "react-router-dom";
import { api, fileUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { KeyVisualPicker } from "@/components/builder/KeyVisualPicker";
import { PlatformLinksEditor } from "@/components/builder/PlatformLinksEditor";
import { TourStopsEditor } from "@/components/builder/TourStopsEditor";
import { SocialLinksEditor } from "@/components/builder/SocialLinksEditor";
import { MetaPixelsEditor } from "@/components/builder/MetaPixelsEditor";
import { AnalyticsPanel } from "@/components/analytics/AnalyticsPanel";
import { FanPageView } from "@/components/fanpage/FanPageView";
import { LinkHealthDialog } from "@/components/builder/LinkHealthDialog";
import { HygieneCheckDialog } from "@/components/builder/HygieneCheckDialog";
import { FlyerExtractDialog } from "@/components/builder/FlyerExtractDialog";
import { runLinkHealthCheck } from "@/lib/linkHealth";
import { runHygieneCheck } from "@/lib/hygieneCheck";
import { extractFromFlyer } from "@/lib/flyerExtract";
import { v4 as uuid } from "@/lib/uuid";
import { HEADLINE_FONTS } from "@/constants/fonts";
import { loadHeadlineFont } from "@/lib/loadHeadlineFont";
import { toast } from "sonner";

const BACKGROUND_STYLES = [
  { value: "gradient", label: "Gradient Glow" },
  { value: "solid", label: "Solid Color" },
  { value: "blurred_cover", label: "Blurred Cover" },
];

export default function PageBuilder() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [page, setPage] = useState(null);
  const [saving, setSaving] = useState(false);
  const [slugError, setSlugError] = useState("");
  const [handleError, setHandleError] = useState("");
  const [healthDialog, setHealthDialog] = useState({ open: false, brokenLinks: [] });
  const [hygieneDialog, setHygieneDialog] = useState({ open: false, mismatches: [] });
  const [flyerDialog, setFlyerDialog] = useState({ open: false, extracted: null });
  const [extracting, setExtracting] = useState(false);
  const [applyingFlyer, setApplyingFlyer] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [pixelPresets, setPixelPresets] = useState([]);
  const wasNeverSaved = useRef(!!location.state?.justCreated);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/pages/${id}`);
      if (!location.state?.justCreated) {
        wasNeverSaved.current = data.created_at === data.updated_at;
      }
      setPage(data);
    } catch {
      toast.error("Page not found");
      navigate("/dashboard");
    }
  }, [id, navigate, location.state]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    api.get("/pixel-presets").then(({ data }) => setPixelPresets(data)).catch(() => {});
  }, []);

  const patch = (fields) => setPage((p) => ({ ...p, ...fields }));

  const handleSavePixelPreset = async (pixel) => {
    try {
      const { data } = await api.post("/pixel-presets", { pixel_id: pixel.pixel_id, label: pixel.label });
      setPixelPresets((prev) => {
        const exists = prev.some((p) => p.pixel_id === data.pixel_id);
        return exists ? prev.map((p) => (p.pixel_id === data.pixel_id ? data : p)) : [...prev, data];
      });
      toast.success("Saved as preset");
    } catch {
      toast.error("Could not save preset");
    }
  };

  const handleUsePixelPreset = (preset) => {
    if (page.meta_pixels.some((p) => p.pixel_id === preset.pixel_id)) {
      toast.info("That pixel is already on this page");
      return;
    }
    patch({ meta_pixels: [...page.meta_pixels, { id: uuid(), pixel_id: preset.pixel_id, label: preset.label }] });
  };

  const handleDeletePixelPreset = async (presetId) => {
    try {
      await api.delete(`/pixel-presets/${presetId}`);
      setPixelPresets((prev) => prev.filter((p) => p.id !== presetId));
      toast.success("Preset removed");
    } catch {
      toast.error("Could not remove preset");
    }
  };

  const saveChanges = async (overridePage) => {
    const p = overridePage || page;
    if (!p.slug || !/^[a-z0-9-]+$/.test(p.slug)) {
      setSlugError("Slug can only contain lowercase letters, numbers, and hyphens");
      return false;
    }
    setSaving(true);
    try {
      const { data: slugCheck } = await api.get(`/pages/check-slug`, { params: { slug: p.slug, exclude_id: p.id } });
      if (!slugCheck.available) {
        setSlugError("This slug is already taken");
        setSaving(false);
        return false;
      }
      setSlugError("");
      await api.put(`/pages/${id}`, {
        title: p.title,
        slug: p.slug,
        url_handle: p.url_handle,
        description: p.description,
        artist_name: p.artist_name,
        event_name: p.event_name,
        source_link: p.source_link,
        background_style: p.background_style,
        platform_links: p.platform_links,
        tour_stops: p.tour_stops,
        social_links: p.social_links,
        meta_pixels: p.meta_pixels,
        colors: p.colors,
        headline_font: p.headline_font || undefined,
      });
      return true;
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (Array.isArray(detail)) {
        const handleErr = detail.find((d) => d.loc?.includes("url_handle"));
        if (handleErr) {
          setHandleError(handleErr.msg.replace(/^Value error,\s*/, ""));
          return false;
        }
      }
      toast.error((typeof detail === "string" && detail) || "Could not save page");
      return false;
    } finally {
      setSaving(false);
    }
  };

  const handleSaveClick = async () => {
    const promptPublish = wasNeverSaved.current;
    const success = await saveChanges();
    if (success) {
      navigate(`/pages/${id}/share`, { state: { promptPublish } });
    }
  };

  const handleExtractFromFlyer = async () => {
    setExtracting(true);
    try {
      const data = await extractFromFlyer(id);
      setFlyerDialog({ open: true, extracted: data });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not read the flyer");
    } finally {
      setExtracting(false);
    }
  };

  const handleApplyFlyer = async () => {
    const extracted = flyerDialog.extracted;
    setApplyingFlyer(true);
    const existingStop = page.tour_stops[0];
    const mergedStop = existingStop
      ? {
          ...existingStop,
          date: extracted.date || existingStop.date,
          end_date: extracted.end_date || existingStop.end_date,
          date_mode: extracted.end_date ? "range" : existingStop.date_mode,
          start_time: extracted.start_time || existingStop.start_time,
          venue: extracted.venue || existingStop.venue,
          city: extracted.city || existingStop.city,
        }
      : {
          id: uuid(),
          date_mode: extracted.end_date ? "range" : "single",
          date: extracted.date || "",
          end_date: extracted.end_date || "",
          start_time: extracted.start_time || "",
          timezone: "Asia/Kolkata",
          city: extracted.city || "",
          venue: extracted.venue || "",
          ticket_url: "",
          cta_label: "Get Tickets",
          currency: "INR",
          price: null,
          order: 0,
        };
    const newStops = existingStop ? [mergedStop, ...page.tour_stops.slice(1)] : [mergedStop];
    const newPage = {
      ...page,
      event_name: extracted.event_name || page.event_name,
      tour_stops: newStops,
    };
    setPage(newPage);
    setFlyerDialog({ open: false, extracted: null });
    const success = await saveChanges(newPage);
    setApplyingFlyer(false);
    toast[success ? "success" : "error"](success ? "Applied and saved!" : "Applied, but could not save — please hit Save manually");
  };

  const doPublish = async (status) => {
    setPublishing(true);
    try {
      const { data } = await api.post(`/pages/${id}/publish`, { status });
      setPage(data);
      toast.success(status === "published" ? "Page published!" : "Moved to draft");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not update publish status");
    } finally {
      setPublishing(false);
    }
  };

  const handlePublishToggle = async (checked) => {
    const status = checked ? "published" : "draft";
    const success = await saveChanges();
    if (!success) return;
    if (status === "published") {
      const broken = await runLinkHealthCheck(id);
      if (broken.length > 0) {
        setHealthDialog({ open: true, brokenLinks: broken });
        return;
      }
      if (page.type === "event") {
        const mismatches = await runHygieneCheck(id);
        if (mismatches.length > 0) {
          setHygieneDialog({ open: true, mismatches });
          return;
        }
      }
    }
    await doPublish(status);
  };

  const handlePublishAnyway = async () => {
    setHealthDialog({ open: false, brokenLinks: [] });
    await doPublish("published");
  };

  const handleHygienePublishAnyway = async () => {
    setHygieneDialog({ open: false, mismatches: [] });
    await doPublish("published");
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(publicUrl());
    toast.success(page.status === "published" ? "Link copied" : "Link copied — remember to publish to make it live!");
  };

  const handleViewPage = () => {
    if (page.status === "published") {
      window.open(publicUrl(), "_blank");
    } else {
      window.open(`${window.location.origin}/preview/${page.id}`, "_blank");
    }
  };

  const publicUrl = () => (page.url_handle ? `${window.location.origin}/${page.url_handle}/${page.slug}` : `${window.location.origin}/s/${page.slug}`);

  if (!page) {
    return (
      <div className="min-h-screen bg-[#0B0E14] flex items-center justify-center">
        <div className="h-8 w-8 rounded-full border-2 border-indigo-400 border-t-transparent animate-spin" />
      </div>
    );
  }

  const isEvent = page.type === "event";

  return (
    <div className="min-h-screen bg-[#0B0E14] text-white" data-testid="page-builder">
      <nav className="border-b border-white/10 px-6 py-4 flex items-center justify-between gap-4 flex-wrap">
        <Link to="/dashboard" className="text-slate-400 hover:text-white shrink-0" data-testid="builder-back-link">
          <i className="fa-solid fa-arrow-left mr-2" />Dashboard
        </Link>

        <div className="flex items-center gap-2 text-xs font-mono-slug text-slate-400 bg-white/5 rounded-full px-4 py-1.5 min-w-0">
          <span className="truncate" data-testid="public-page-url">{page.url_handle ? `/${page.url_handle}/${page.slug}` : `/s/${page.slug}`}</span>
          <button onClick={handleCopyLink} className="text-slate-300 hover:text-white shrink-0" title="Copy link" data-testid="copy-public-url-button">
            <i className="fa-solid fa-copy" />
          </button>
          <button onClick={handleViewPage} className="text-slate-300 hover:text-white shrink-0" title={page.status === "published" ? "View live page" : "Preview draft"} data-testid="view-public-page-button">
            <i className="fa-solid fa-arrow-up-right-from-square" />
          </button>
        </div>

        <div className="flex items-center gap-3 shrink-0">
          <div className="flex items-center gap-2 text-sm">
            <span className="text-slate-400">{page.status === "published" ? "Published" : "Draft"}</span>
            <Switch checked={page.status === "published"} onCheckedChange={handlePublishToggle} data-testid="publish-toggle-switch" />
          </div>
          <Button onClick={handleSaveClick} disabled={saving} className="bg-indigo-500 hover:bg-indigo-600 rounded-full px-5" data-testid="save-page-button">
            {saving ? "Saving..." : "Save"}
          </Button>
        </div>
      </nav>

      <div className="grid lg:grid-cols-2 gap-0 lg:h-[calc(100vh-65px)]">
        <div className="overflow-y-auto p-6 space-y-6 lg:h-full">
          <KeyVisualPicker
            pageId={page.id}
            type={page.type}
            coverImageUrl={page.cover_image_url}
            sourceLink={page.source_link}
            onUpdated={(fields) => patch(fields)}
          />

          {isEvent && (
            <Button
              type="button"
              variant="outline"
              onClick={handleExtractFromFlyer}
              disabled={!page.cover_image_url || extracting}
              className="w-full border-indigo-400/40 text-indigo-300 hover:bg-indigo-500/10 border-dashed"
              data-testid="auto-fill-from-flyer-button"
            >
              <i className={`fa-solid ${extracting ? "fa-spinner fa-spin" : "fa-wand-magic-sparkles"} mr-2`} />
              {extracting ? "Reading your flyer..." : "Auto-fill from Flyer"}
            </Button>
          )}

          <Tabs defaultValue="details" className="w-full">
            <TabsList className="bg-white/5 w-full justify-start" data-testid="builder-tabs">
              <TabsTrigger value="details" data-testid="builder-tab-details">Details</TabsTrigger>
              <TabsTrigger value="links" data-testid="builder-tab-links">{isEvent ? "Tour Stops" : "Links"}</TabsTrigger>
              <TabsTrigger value="social" data-testid="builder-tab-social">Social</TabsTrigger>
              <TabsTrigger value="settings" data-testid="builder-tab-settings">Settings</TabsTrigger>
              <TabsTrigger value="analytics" data-testid="builder-tab-analytics">Analytics</TabsTrigger>
            </TabsList>

            <TabsContent value="details" className="space-y-4 pt-4">
              {isEvent ? (
                <div className="space-y-1.5">
                  <Label>Event Name</Label>
                  <Input value={page.event_name} onChange={(e) => patch({ event_name: e.target.value })} className="bg-white/5 border-white/15" data-testid="event-name-input" />
                </div>
              ) : (
                <>
                  <div className="space-y-1.5">
                    <Label>Release Title</Label>
                    <Input value={page.title} onChange={(e) => patch({ title: e.target.value })} className="bg-white/5 border-white/15" data-testid="release-title-input" />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Artist Name</Label>
                    <Input value={page.artist_name} onChange={(e) => patch({ artist_name: e.target.value })} className="bg-white/5 border-white/15" data-testid="artist-name-input" />
                  </div>
                </>
              )}
              <div className="space-y-1.5">
                <Label>Description</Label>
                <Textarea value={page.description} onChange={(e) => patch({ description: e.target.value })} className="bg-white/5 border-white/15" rows={3} data-testid="description-input" />
              </div>
              <div className="space-y-1.5">
                <Label>Public URL</Label>
                <div className="flex items-center gap-1 text-sm font-mono-slug text-slate-400 flex-wrap">
                  <span className="shrink-0">{window.location.host}/</span>
                  <Input
                    value={page.url_handle}
                    onChange={(e) => { patch({ url_handle: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "") }); setHandleError(""); }}
                    placeholder="artist-handle"
                    className="bg-white/5 border-white/15 font-mono-slug w-32"
                    data-testid="handle-input"
                  />
                  <span className="shrink-0">/</span>
                  <Input
                    value={page.slug}
                    onChange={(e) => { patch({ slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "") }); setSlugError(""); }}
                    className="bg-white/5 border-white/15 font-mono-slug"
                    data-testid="slug-input"
                  />
                </div>
                {handleError && <p className="text-xs text-rose-400" data-testid="handle-error">{handleError}</p>}
                {slugError && <p className="text-xs text-rose-400" data-testid="slug-error">{slugError}</p>}
                <p className="text-xs text-slate-500" data-testid="public-url-hint">
                  {page.url_handle
                    ? `Live at /${page.url_handle}/${page.slug} — the old /s/${page.slug} link still works and redirects here.`
                    : `Add a handle (e.g. your artist name) for a custom link like /your-handle/${page.slug}. Leave blank to use /s/${page.slug}.`}
                </p>
              </div>
            </TabsContent>

            <TabsContent value="links" className="pt-4">
              {isEvent ? (
                <TourStopsEditor stops={page.tour_stops} onChange={(stops) => patch({ tour_stops: stops })} />
              ) : (
                <PlatformLinksEditor links={page.platform_links} onChange={(links) => patch({ platform_links: links })} />
              )}
            </TabsContent>

            <TabsContent value="social" className="pt-4">
              <SocialLinksEditor links={page.social_links} onChange={(links) => patch({ social_links: links })} />
            </TabsContent>

            <TabsContent value="settings" className="space-y-5 pt-4">
              <div className="space-y-1.5">
                <Label>Background Style</Label>
                <div className="grid grid-cols-3 gap-2">
                  {BACKGROUND_STYLES.map((b) => (
                    <button
                      key={b.value}
                      onClick={() => patch({ background_style: b.value })}
                      className={`rounded-lg border px-3 py-2 text-xs font-medium transition-colors ${page.background_style === b.value ? "border-indigo-400 bg-indigo-500/15 text-white" : "border-white/10 text-slate-400 hover:bg-white/5"}`}
                      data-testid={`background-style-${b.value}`}
                    >
                      {b.label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="space-y-1.5">
                <Label>Accent Color (CTA button & date badge)</Label>
                <div className="flex items-center gap-3">
                  <input
                    type="color"
                    value={page.colors?.accent || "#6366F1"}
                    onChange={(e) => patch({ colors: { ...page.colors, accent: e.target.value } })}
                    className="h-10 w-14 rounded-lg border border-white/15 bg-transparent cursor-pointer p-0.5"
                    data-testid="accent-color-picker"
                  />
                  <Input
                    value={page.colors?.accent || "#6366F1"}
                    onChange={(e) => patch({ colors: { ...page.colors, accent: e.target.value } })}
                    placeholder="#6366F1"
                    maxLength={7}
                    className="flex-1 bg-white/5 border-white/15 font-mono-slug uppercase"
                    data-testid="accent-color-hex-input"
                  />
                </div>
                <p className="text-xs text-slate-500">Auto-picked from your artwork on upload — override it here anytime.</p>
              </div>

              <div className="space-y-1.5">
                <Label>Headline & Text Color</Label>
                <div className="flex items-center gap-3">
                  <input
                    type="color"
                    value={page.colors?.text || "#FFFFFF"}
                    onChange={(e) => patch({ colors: { ...page.colors, text: e.target.value } })}
                    className="h-10 w-14 rounded-lg border border-white/15 bg-transparent cursor-pointer p-0.5"
                    data-testid="text-color-picker"
                  />
                  <Input
                    value={page.colors?.text || "#FFFFFF"}
                    onChange={(e) => patch({ colors: { ...page.colors, text: e.target.value } })}
                    placeholder="#FFFFFF"
                    maxLength={7}
                    className="flex-1 bg-white/5 border-white/15 font-mono-slug uppercase"
                    data-testid="text-color-hex-input"
                  />
                </div>
                <p className="text-xs text-slate-500">Controls the title, artist/event name, and description text color on your page.</p>
              </div>

              <div className="space-y-1.5">
                <Label>Headline Font</Label>
                <Select
                  value={page.headline_font || (page.type === "event" ? "Syne" : "Outfit")}
                  onValueChange={(value) => patch({ headline_font: value })}
                  onOpenChange={(open) => { if (open) HEADLINE_FONTS.forEach((f) => loadHeadlineFont(f.id)); }}
                >
                  <SelectTrigger className="bg-white/5 border-white/15" data-testid="headline-font-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {HEADLINE_FONTS.map((f) => (
                      <SelectItem key={f.id} value={f.id} data-testid={`headline-font-option-${f.id.replace(/\s+/g, "-").toLowerCase()}`}>
                        <span style={{ fontFamily: `"${f.id}", sans-serif` }} className="text-base">{f.label}</span>
                        <span className="text-slate-500 text-xs ml-2">{f.vibe}</span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-slate-500">Changes the display font used for your page's title — updates the live preview instantly.</p>
              </div>

              <MetaPixelsEditor
                pixels={page.meta_pixels || []}
                onChange={(pixels) => patch({ meta_pixels: pixels })}
                presets={pixelPresets}
                onSavePreset={handleSavePixelPreset}
                onUsePreset={handleUsePixelPreset}
                onDeletePreset={handleDeletePixelPreset}
              />
            </TabsContent>

            <TabsContent value="analytics" className="pt-4">
              <AnalyticsPanel pageId={page.id} />
            </TabsContent>
          </Tabs>
        </div>

        <div className="hidden lg:block bg-black/40 border-l border-white/10 overflow-y-auto">
          <div className="sticky top-0 p-3 text-center text-xs text-slate-500 uppercase tracking-widest bg-black/40 backdrop-blur border-b border-white/5">Live Preview</div>
          <FanPageView page={page} scale="preview" />
        </div>
      </div>

      <LinkHealthDialog
        open={healthDialog.open}
        brokenLinks={healthDialog.brokenLinks}
        onCancel={() => setHealthDialog({ open: false, brokenLinks: [] })}
        onPublishAnyway={handlePublishAnyway}
        publishing={publishing}
      />

      <HygieneCheckDialog
        open={hygieneDialog.open}
        mismatches={hygieneDialog.mismatches}
        onCancel={() => setHygieneDialog({ open: false, mismatches: [] })}
        onPublishAnyway={handleHygienePublishAnyway}
        publishing={publishing}
      />

      <FlyerExtractDialog
        open={flyerDialog.open}
        extracted={flyerDialog.extracted}
        onCancel={() => setFlyerDialog({ open: false, extracted: null })}
        onApply={handleApplyFlyer}
        applying={applyingFlyer}
      />
    </div>
  );
}
